import { useEffect, useMemo } from "react";
import { useFragment, useRelayEnvironment, useSubscription } from "react-relay";
import { graphql, requestSubscription } from "relay-runtime";
import { bigintToFixed } from "rujira.js";
import { useNotifications } from "../services/notifcation";
import { TradeSubscriptionsFragment$key } from "./__generated__/TradeSubscriptionsFragment.graphql";
import { TradeSubscriptionsSubscription } from "./__generated__/TradeSubscriptionsSubscription.graphql";

export const fragment = graphql`
  fragment TradeSubscriptionsFragment on Account {
    address
    fin {
      ordersV2(first: 100)
        @connection(key: "TradeSubscriptionsFragment_ordersV2") {
        __id
        edges {
          node {
            __typename
            ... on FinOrder {
              pair {
                address
              }
              side
              rate
              type
              owner
              deviation
              remaining
              filled
              ...LimitOrdersFragment
            }
            ... on CalcOrder {
              id
              updatedAt
              valueUsd
              source
              status
              balances {
                amount
              }
              config {
                nodes {
                  ... on CalcAction {
                    action {
                      ... on CalcActionSwap {
                        routes {
                          ... on CalcSwapRouteFin {
                            pairAddress
                          }
                        }
                      }
                    }
                  }
                }
              }
              ...RecurringOrdersFragment
            }
          }
        }
      }
    }
  }
`;

const orderSubscription = graphql`
  subscription TradeSubscriptionsSubscription(
    $contract: Address!
    $owner: Address!
    $price: String!
    $side: String!
  ) {
    finOrderFilled(
      contract: $contract
      owner: $owner
      price: $price
      side: $side
    ) {
      node {
        __typename
        ... on FinOrder {
          pair {
            assetBase {
              metadata {
                symbol
              }
            }
            assetQuote {
              metadata {
                symbol
              }
            }
          }
        }

        ...LimitOrdersFragment
      }
    }
  }
`;

const finOrderUpdatedSubscription = graphql`
  subscription TradeSubscriptionsOrderSubscription(
    $connection: ID!
    $owner: Address!
  ) {
    finOrderUpdated(owner: $owner) @appendEdge(connections: [$connection]) {
      node {
        ...LimitOrdersFragment
      }
    }
  }
`;

const calcOrdersUpdatedSubscription = graphql`
  subscription TradeSubscriptionsCalcOrdersUpdatedSubscription(
    $connection: ID!
    $owner: Address!
  ) {
    calcOrdersUpdated(owner: $owner) @appendEdge(connections: [$connection]) {
      node {
        ... on CalcOrder {
          valueUsd
          source
          status
          balances {
            amount
          }
          ...RecurringOrdersFragment
        }
      }
    }
  }
`;

export const useTradeOrderSubscriptions = (
  k?: TradeSubscriptionsFragment$key
) => {
  const env = useRelayEnvironment();
  const account = useFragment(fragment, k);

  useEffect(() => {
    if (!account?.address) return;

    const d = [finOrderUpdatedSubscription, calcOrdersUpdatedSubscription].map(
      (s) =>
        requestSubscription(env, {
          subscription: s,
          variables: {
            owner: account.address,
            connection: account.fin?.ordersV2?.__id,
          },
        })
    );

    return () => {
      d.forEach((d) => d.dispose());
    };
  }, [account]);

  const { push } = useNotifications();

  useEffect(() => {
    const subs = (account?.fin?.ordersV2?.edges || []).map((o) => {
      if (!o?.node || o.node.__typename !== "FinOrder") return null;
      const price = o.node.deviation
        ? { oracle: Number(o.node.deviation) }
        : { fixed: bigintToFixed(o.node.rate) };
      return requestSubscription<TradeSubscriptionsSubscription>(env, {
        subscription: orderSubscription,
        variables: {
          contract: o.node.pair.address,
          owner: o.node.owner,
          price: `${o.node.type}:${price.oracle || price.fixed?.replace(/\.?0+$/, "")}`,
          side: o.node.side,
        },
        onNext: async (e) => {
          if (e?.finOrderFilled?.node?.__typename !== "FinOrder") return;
          push(
            `${e?.finOrderFilled?.node.pair?.assetBase.metadata.symbol}/${e?.finOrderFilled?.node.pair?.assetQuote.metadata.symbol} order filled`
          );
        },
      });
    });
    return () => {
      subs.map((x) => x?.dispose());
    };
  }, [account]);
};

const bookSubscription = graphql`
  subscription TradeSubscriptionsBookSubscription($id: ID!) {
    node(id: $id) {
      ... on FinBook {
        ...OrderBookSubFragment
      }
    }
  }
`;

export const useTradeBookSubscription = (id: string | null | undefined) => {
  if (!id) return;
  const config = useMemo(
    () => ({
      subscription: bookSubscription,
      variables: { id },
    }),
    [id]
  );
  return useSubscription(config);
};
